import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiCheck, FiCalendar } = FiIcons;

const ActionPlanBuilder = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});

  const questions = [
    {
      id: 'stage',
      text: 'What is your current manuscript stage?',
      options: [
        { text: '0–40% draft (early stages)', value: 'early-draft' },
        { text: '40–90% draft (substantial progress)', value: 'mid-draft' },
        { text: '100% draft needs editing', value: 'complete-draft' },
        { text: 'Revised, pre-publish ready', value: 'revised' }
      ]
    },
    {
      id: 'publishing-path',
      text: 'What is your preferred publishing path?',
      options: [
        { text: 'Traditional publishing', value: 'traditional' },
        { text: 'Hybrid publishing', value: 'hybrid' },
        { text: 'Indie publishing', value: 'indie' },
        { text: 'Not sure yet', value: 'not-sure' }
      ]
    },
    {
      id: 'daily-time',
      text: 'How much time can you dedicate daily?',
      options: [
        { text: '20 minutes', value: '20' },
        { text: '40 minutes', value: '40' },
        { text: '60+ minutes', value: '60' }
      ]
    },
    {
      id: 'constraints',
      text: 'Do you have any constraints this week?',
      options: [
        { text: 'Heavy week ahead', value: 'heavy' },
        { text: 'Travel/family commitments', value: 'travel' },
        { text: 'Low energy season', value: 'low-energy' },
        { text: 'No major constraints', value: 'none' }
      ]
    },
    {
      id: 'energy-rhythm',
      text: 'When is your peak energy?',
      options: [
        { text: 'Morning person', value: 'am' },
        { text: 'Evening person', value: 'pm' },
        { text: 'Varies by day', value: 'varies' }
      ]
    },
    {
      id: 'obstacle',
      text: 'What is your biggest obstacle right now?',
      options: [
        { text: 'Clarity on message/direction', value: 'clarity' },
        { text: 'Focus and consistency', value: 'focus' },
        { text: 'Writing craft skills', value: 'craft' },
        { text: 'Logistics and planning', value: 'logistics' },
        { text: 'Fear/perfectionism', value: 'fear' }
      ]
    },
    {
      id: 'accountability',
      text: 'What type of accountability works for you?',
      options: [
        { text: 'Private (self-motivated)', value: 'private' },
        { text: 'Buddy system (one person)', value: 'buddy' },
        { text: 'Group accountability', value: 'group' },
        { text: 'Coach/mentor guidance', value: 'coach' }
      ]
    },
    {
      id: 'faith-practice',
      text: 'What faith practices energize you?',
      options: [
        { text: 'Prayer and reflection', value: 'prayer' },
        { text: 'Scripture meditation', value: 'verse' },
        { text: 'Gratitude practice', value: 'gratitude' },
        { text: 'All of the above', value: 'all' }
      ]
    },
    {
      id: 'primary-outcome',
      text: 'What is your primary outcome for this week?',
      options: [
        { text: 'Add significant word count', value: 'add-words' },
        { text: 'Complete self-edit pass', value: 'self-edit' },
        { text: 'Make 3 publishing decisions', value: 'decisions' },
        { text: 'Build launch plan skeleton', value: 'launch' }
      ]
    },
    {
      id: 'secondary-outcome',
      text: 'What is your secondary outcome?',
      options: [
        { text: 'Reader research/feedback', value: 'research' },
        { text: 'Back cover copy/bio', value: 'copy' },
        { text: 'Platform tune-up', value: 'platform' },
        { text: 'Organize project files', value: 'organize' },
        { text: 'None (focus on primary)', value: 'none' }
      ]
    }
  ];

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleResponse = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const generateActionPlan = () => {
    const track = getTrack(responses.stage);
    const subTrack = responses['publishing-path'] !== 'not-sure' ? responses['publishing-path'] : null;
    const timeBlock = responses['daily-time'];
    const constraints = responses.constraints;
    const obstacle = responses.obstacle;
    const accountability = responses.accountability;
    const faithPractice = responses.faithPractice;
    const primaryOutcome = responses['primary-outcome'];
    const secondaryOutcome = responses['secondary-outcome'];
    const energyRhythm = responses['energy-rhythm'];

    const actionPlan = {
      track,
      subTrack,
      timeBlock,
      constraints,
      obstacle,
      accountability,
      faithPractice,
      primaryOutcome,
      secondaryOutcome,
      energyRhythm,
      days: generateDayPlans({
        track,
        subTrack,
        timeBlock,
        constraints,
        obstacle,
        accountability,
        faithPractice,
        primaryOutcome,
        secondaryOutcome,
        energyRhythm
      })
    };

    return actionPlan;
  };

  const getTrack = (stage) => {
    const tracks = {
      'early-draft': 'Draft Momentum',
      'mid-draft': 'Finish & Stabilize',
      'complete-draft': 'Editing Foundations',
      'revised': 'Pre-Publish Prep'
    };
    return tracks[stage] || 'Draft Momentum';
  };

  const generateDayPlans = (config) => {
    const scriptures = [
      { verse: 'The one who calls you is faithful, and he will do it.', reference: '1 Thessalonians 5:24' },
      { verse: 'Commit to the Lord whatever you do, and he will establish your plans.', reference: 'Proverbs 16:3' },
      { verse: 'Let us not become weary in doing good, for at the proper time we will reap a harvest if we do not give up.', reference: 'Galatians 6:9' },
      { verse: 'May the favor of the Lord our God rest on us; establish the work of our hands for us—yes, establish the work of our hands.', reference: 'Psalm 90:17' },
      { verse: 'Whatever you do, work at it with all your heart, as working for the Lord, not for human masters.', reference: 'Colossians 3:23' },
      { verse: 'If any of you lacks wisdom, you should ask God, who gives generously to all without finding fault, and it will be given to you.', reference: 'James 1:5' },
      { verse: 'Being confident of this, that he who began a good work in you will carry it on to completion until the day of Christ Jesus.', reference: 'Philippians 1:6' }
    ];

    const days = [];
    const hasConstraints = config.constraints !== 'none';
    let graceDayAdded = false;

    for (let i = 1; i <= 7; i++) {
      const isGraceDay = hasConstraints && i === 4 && !graceDayAdded;
      if (isGraceDay) graceDayAdded = true;

      const day = {
        day: i,
        isGraceDay,
        warmUp: getWarmUp(config, i),
        coreTask: getCoreTask(config, i, isGraceDay),
        wrapUp: getWrapUp(config),
        accountability: getAccountabilityTip(config.accountability),
        faithElement: getFaithElement(config.faithPractice, scriptures[i - 1]),
        energyNote: config.energyRhythm !== 'varies' ? 
          (config.energyRhythm === 'am' ? 'Best run in the morning' : 'Evening focus will help') : null
      };

      days.push(day);
    }

    return days;
  };

  const getWarmUp = (config, day) => {
    if (config.obstacle === 'fear' && day === 1) {
      return 'Set a 15-minute timer and write "messy first draft" - no editing allowed';
    }
    if (config.obstacle === 'clarity' && day === 1) {
      return 'Write 3 sentences about your book\'s core message and target reader';
    }
    return 'Quick prayer for wisdom and clarity';
  };

  const getCoreTask = (config, day, isGraceDay) => {
    if (isGraceDay) {
      return { task: 'Light review and reflection (15 minutes)', minutes: 15 };
    }

    const timeMinutes = parseInt(config.timeBlock);
    let task = '';

    // Track-specific tasks
    if (config.track === 'Draft Momentum') {
      const tasks = [
        'Write new content focusing on chapter development',
        'Outline next 2-3 chapters',
        'Write dialogue or key scene',
        'Review and strengthen opening chapter',
        'Develop character or theme depth',
        'Write transitional sections',
        'Review week\'s progress and plan next week'
      ];
      task = tasks[day - 1];
    } else if (config.track === 'Finish & Stabilize') {
      const tasks = [
        'Complete incomplete chapters',
        'Strengthen chapter transitions',
        'Refine manuscript structure',
        'Review pacing and flow',
        'Polish opening and closing',
        'Final content review',
        'Create revision checklist'
      ];
      task = tasks[day - 1];
    } else if (config.track === 'Editing Foundations') {
      const tasks = [
        'Content edit for message clarity',
        'Review theological accuracy',
        'Strengthen chapter purposes',
        'Improve narrative flow',
        'Refine author voice consistency',
        'Address reader feedback',
        'Prepare for professional editing'
      ];
      task = tasks[day - 1];
    } else {
      const tasks = [
        'Finalize book proposal outline',
        'Develop marketing plan',
        'Build author platform content',
        'Research publishing options',
        'Create launch timeline',
        'Prepare submission materials',
        'Review final production checklist'
      ];
      task = tasks[day - 1];
    }

    return { task, minutes: timeMinutes };
  };

  const getWrapUp = (config) => {
    return 'Record progress and tomorrow\'s focus in journal';
  };

  const getAccountabilityTip = (type) => {
    const tips = {
      private: 'Check in with yourself - celebrate small wins',
      buddy: 'Send progress update to your accountability partner',
      group: 'Share one win in your writing group',
      coach: 'Email weekly summary to your coach/mentor'
    };
    return tips[type] || tips.private;
  };

  const getFaithElement = (practice, scripture) => {
    if (practice === 'all') {
      const practices = ['Prayer focus', 'Verse meditation', 'Gratitude reflection'];
      return practices[Math.floor(Math.random() * practices.length)] + ': "' + scripture.verse + '" - ' + scripture.reference;
    }
    const labels = {
      prayer: 'Prayer focus',
      verse: 'Verse meditation', 
      gratitude: 'Gratitude reflection'
    };
    return `${labels[practice]}: "${scripture.verse}" - ${scripture.reference}`;
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const actionPlan = generateActionPlan();
      onComplete(responses, actionPlan);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const isCurrentQuestionAnswered = () => {
    return responses[currentQ.id] !== undefined;
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-700">
              Question {currentQuestion + 1} of {questions.length}
            </h2>
            <span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Question Card */}
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-xl p-8 mb-8"
        >
          <div className="flex items-start gap-3 mb-6">
            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiCalendar} className="text-xl text-white" />
            </div>
            <div className="flex-grow">
              <h3 className="text-2xl font-serif text-gray-900 leading-relaxed">
                {currentQ.text}
              </h3>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {currentQ.options.map((option, index) => {
              const isSelected = responses[currentQ.id] === option.value;

              return (
                <motion.button
                  key={option.value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleResponse(currentQ.id, option.value)}
                  className={`w-full p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    isSelected
                      ? 'border-purple-500 bg-purple-50 shadow-lg'
                      : 'border-gray-200 hover:border-purple-500/50 hover:bg-purple-5'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                      {option.text}
                    </span>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center"
                      >
                        <SafeIcon icon={FiCheck} className="text-sm" />
                      </motion.div>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
              currentQuestion === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:text-purple-600 hover:bg-purple-5'
            }`}
          >
            Previous
          </button>
          
          <button
            onClick={handleNext}
            disabled={!isCurrentQuestionAnswered()}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
              isCurrentQuestionAnswered()
                ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {currentQuestion === questions.length - 1 ? 'Generate Action Plan' : 'Next Question'}
            <SafeIcon icon={FiArrowRight} className="text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionPlanBuilder;